const TIME_WINDOW_VALUES = new Set(['24h', '7d', '30d', 'all']);
const OUTCOME_VALUES = new Set(['resolved', 'escalated', 'pending']);

export const ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS = [
  'override_drilldown',
  'attribute_override_rule_index',
  'attribute_override_label',
  'attribute_override_action',
  'attribute_override_match_type',
  'attribute_override_outcome',
  'attribute_override_condition',
  'attribute_override_guard',
  'attribute_override_fix_intent_code',
  'attribute_override_fix_intent_title',
  'attribute_override_compare_stage_code',
  'attribute_override_compare_stage_title',
  'inbox_id',
  'conversation_id',
  'time_window',
];

const normalizeString = value => String(value || '').trim();

const normalizePositiveInteger = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? String(Math.trunc(numeric))
    : '';
};

const normalizeTimeWindow = value => {
  const normalized = normalizeString(value);
  return TIME_WINDOW_VALUES.has(normalized) ? normalized : '';
};

const normalizeOutcome = value => {
  const normalized = normalizeString(value).toLowerCase();
  return OUTCOME_VALUES.has(normalized) ? normalized : '';
};

const assignOrDelete = (target, key, value) => {
  const normalized = normalizeString(value);
  if (normalized) target[key] = normalized;
  else delete target[key];
};

export const parseAttributeOverrideDrilldownQuery = query => ({
  enabled: normalizeString(query?.override_drilldown) === '1',
  ruleIndex: normalizePositiveInteger(query?.attribute_override_rule_index),
  label: normalizeString(query?.attribute_override_label),
  action: normalizeString(query?.attribute_override_action),
  matchType: normalizeString(query?.attribute_override_match_type),
  outcome: normalizeOutcome(query?.attribute_override_outcome),
  condition: normalizeString(query?.attribute_override_condition),
  guard: normalizeString(query?.attribute_override_guard),
  fixIntentCode: normalizeString(query?.attribute_override_fix_intent_code),
  fixIntentTitle: normalizeString(query?.attribute_override_fix_intent_title),
  compareStageCode: normalizeString(query?.attribute_override_compare_stage_code),
  compareStageTitle: normalizeString(query?.attribute_override_compare_stage_title),
  inboxId: normalizePositiveInteger(query?.inbox_id),
  conversationId: normalizePositiveInteger(query?.conversation_id),
  timeWindow: normalizeTimeWindow(query?.time_window),
});

export const buildAttributeOverrideDrilldownQuery = (
  baseQuery = {},
  options = {}
) => {
  const nextQuery = { ...baseQuery, override_drilldown: '1' };

  assignOrDelete(
    nextQuery,
    'attribute_override_rule_index',
    normalizePositiveInteger(options.ruleIndex)
  );
  assignOrDelete(nextQuery, 'attribute_override_label', options.label);
  assignOrDelete(nextQuery, 'attribute_override_action', options.action);
  assignOrDelete(nextQuery, 'attribute_override_match_type', options.matchType);
  assignOrDelete(
    nextQuery,
    'attribute_override_outcome',
    normalizeOutcome(options.outcome)
  );
  assignOrDelete(nextQuery, 'attribute_override_condition', options.condition);
  assignOrDelete(nextQuery, 'attribute_override_guard', options.guard);
  assignOrDelete(
    nextQuery,
    'attribute_override_fix_intent_code',
    options.fixIntentCode
  );
  assignOrDelete(
    nextQuery,
    'attribute_override_fix_intent_title',
    options.fixIntentTitle
  );
  assignOrDelete(
    nextQuery,
    'attribute_override_compare_stage_code',
    options.compareStageCode
  );
  assignOrDelete(
    nextQuery,
    'attribute_override_compare_stage_title',
    options.compareStageTitle
  );
  assignOrDelete(
    nextQuery,
    'inbox_id',
    normalizePositiveInteger(options.inboxId)
  );
  assignOrDelete(
    nextQuery,
    'conversation_id',
    normalizePositiveInteger(options.conversationId)
  );
  assignOrDelete(
    nextQuery,
    'time_window',
    normalizeTimeWindow(options.timeWindow)
  );

  return nextQuery;
};

export const clearAttributeOverrideDrilldownQuery = (baseQuery = {}) => {
  const nextQuery = { ...baseQuery };
  ATTRIBUTE_OVERRIDE_DRILLDOWN_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  return nextQuery;
};
